<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const emit = defineEmits(['export']);

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const dialogRef = ref(null);

const segments = useMapGetter('customViews/getContactCustomViews');
const appliedFilters = useMapGetter('contacts/getAppliedContactFilters');
const uiFlags = useMapGetter('contacts/getUIFlags');
const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabels');
const isExportingContact = computed(() => uiFlags.value.isExporting);

const activeSegmentId = computed(() => route.params.segmentId);
const activeSegment = computed(() =>
  activeSegmentId.value
    ? segments.value.find(view => view.id === Number(activeSegmentId.value))
    : undefined
);

const selectedInboxId = ref(null);
const selectedLabel = ref(route.params.label || '');
const startDate = ref('');
const endDate = ref('');

const inboxOptions = computed(() => [
  {
    value: null,
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.ALL_INBOXES'),
  },
  ...inboxes.value.map(inbox => ({ value: inbox.id, label: inbox.name })),
]);

const labelOptions = computed(() => [
  {
    value: '',
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.ALL_LABELS'),
  },
  ...[...labels.value]
    .sort((a, b) => a.title.localeCompare(b.title))
    .map(label => ({ value: label.title, label: label.title })),
]);

const exportContacts = async () => {
  let query = { payload: [] };

  if (activeSegmentId.value && activeSegment.value) {
    query = activeSegment.value.query;
  } else if (Object.keys(appliedFilters.value).length > 0) {
    query = filterQueryGenerator(appliedFilters.value);
  }

  emit('export', {
    ...query,
    label: selectedLabel.value || '',
    inbox_id: selectedInboxId.value || undefined,
    start_date: startDate.value || undefined,
    end_date: endDate.value || undefined,
  });
};

onMounted(() => {
  if (!labels.value.length) store.dispatch('labels/get');
});

const handleDialogConfirm = async () => {
  await exportContacts();
  dialogRef.value?.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.TITLE')"
    :description="
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.DESCRIPTION')
    "
    :confirm-button-label="
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.CONFIRM')
    "
    :is-loading="isExportingContact"
    :disable-confirm-button="isExportingContact"
    @confirm="handleDialogConfirm"
  >
    <div class="flex flex-col gap-4 pt-2">
      <!-- Label filter -->
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.LABEL_LABEL') }}
        </label>
        <ComboBox
          v-model="selectedLabel"
          :options="labelOptions"
          :placeholder="
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.ALL_LABELS')
          "
          :search-placeholder="
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.SEARCH_LABEL')
          "
        />
      </div>

      <!-- Inbox filter -->
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.INBOX_LABEL') }}
        </label>
        <ComboBox
          v-model="selectedInboxId"
          :options="inboxOptions"
          :placeholder="
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.ALL_INBOXES')
          "
        />
      </div>

      <!-- Date range filter -->
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.DATE_RANGE_LABEL')
          }}
        </label>
        <div class="flex gap-2">
          <div class="flex flex-col gap-1 flex-1">
            <span class="text-xs text-n-slate-10">
              {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.DATE_FROM') }}
            </span>
            <input
              v-model="startDate"
              type="date"
              :max="endDate || undefined"
              class="w-full rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-strong px-3 py-2 text-sm text-n-slate-12 h-9 focus:outline-n-brand"
            />
          </div>
          <div class="flex flex-col gap-1 flex-1">
            <span class="text-xs text-n-slate-10">
              {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.DATE_TO') }}
            </span>
            <input
              v-model="endDate"
              type="date"
              :min="startDate || undefined"
              class="w-full rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-strong px-3 py-2 text-sm text-n-slate-12 h-9 focus:outline-n-brand"
            />
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
